/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Flex, Text, TextField } from '@servicetitan/anvil2';
import { NitrogenPressureTestRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const NitrogenPressureTestForm: FC<{
    className?: string;
    onSubmit?: (params: NitrogenPressureTestRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            initial_pressure: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            initial_temperature: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            final_pressure: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            final_temperature: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: NitrogenPressureTestRequestDto = {
            tool_name: 'nitrogen-pressure-test',
            tool_input: {
                initial_pressure: Number(formState.$.initial_pressure.$),
                initial_temperature: Number(formState.$.initial_temperature.$),
                final_pressure: Number(formState.$.final_pressure.$),
                final_temperature: Number(formState.$.final_temperature.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                label="Starting Pressure (PSIG)"
                type="number"
                value={formState.$.initial_pressure.value}
                onChange={e => formState.$.initial_pressure.onChange(e.target.value)}
            />
            <TextField
                required
                label="Starting Temperature (°F)"
                type="number"
                value={formState.$.initial_temperature.value}
                onChange={e => formState.$.initial_temperature.onChange(e.target.value)}
            />
            <TextField
                required
                label="Ending Pressure (PSIG)"
                type="number"
                value={formState.$.final_pressure.value}
                onChange={e => formState.$.final_pressure.onChange(e.target.value)}
            />
            <TextField
                required
                label="Ending Temperature (°F)"
                type="number"
                value={formState.$.final_temperature.value}
                onChange={e => formState.$.final_temperature.onChange(e.target.value)}
            />
            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
